import { ColorScheme } from "@/lib/prompt-colors";
import { cn } from "@/lib/utils";
import { ArrowRightIcon, LucideIcon } from "lucide-react";
import { CSSProperties, ReactNode } from "react";

type ChatSuggestionCardProps = {
  Icon: LucideIcon;
  label: string;
  /** Icon tints, as `getPromptStyle()` hands them out. */
  colors: Pick<ColorScheme, "icon" | "iconBg">;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  style?: CSSProperties;
  /** Overlay pinned to the card's corners, such as a pin or a status dot. */
  badge?: ReactNode;
};

/**
 * Clickable tile the chat's suggestion blocks are built from: a tinted icon, a
 * label and the arrow that marks it as a shortcut, so every kind of suggestion
 * reads the same.
 */
export function ChatSuggestionCard({
  Icon,
  label,
  colors,
  disabled = false,
  onClick,
  className,
  style,
  badge,
}: ChatSuggestionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      style={style}
      className={cn(
        "group relative flex items-center justify-between gap-2 rounded-xl",
        "border border-border/50 bg-background/50 p-3 text-left text-sm",
        "hover:border-primary/30 hover:bg-background hover:shadow-sm active:bg-muted/50",
        "transition-colors duration-200",
        "focus:outline-none focus:ring-2 focus:ring-primary/50",
        disabled && "pointer-events-none",
        className,
      )}
    >
      {badge}

      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div
          className={cn(
            "shrink-0 rounded-full flex items-center justify-center w-9 h-9",
            "transition-transform duration-200 group-hover:scale-110",
            colors.iconBg,
          )}
        >
          <Icon className={cn("w-5 h-5", colors.icon)} />
        </div>
        <span className="text-muted-foreground line-clamp-2 text-xs">
          {label}
        </span>
      </div>

      <ArrowRightIcon className="size-3.5 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
    </button>
  );
}
